<template>
  <div class="hierarquia-representantes-page">
    <nav class="page-breadcrumb mb-1">
      <feather-icon icon="HomeIcon" size="14" />
      <span class="ml-25">Apoio a Vendas</span>
      <feather-icon icon="ChevronRightIcon" size="14" class="mx-25" />
      <span class="font-weight-bold">Hierarquia de Representantes</span>
    </nav>

    <b-card class="mb-1">
      <div class="d-flex align-items-start">
        <feather-icon icon="InfoIcon" size="18" class="text-primary mr-75 mt-25 flex-shrink-0" />
        <div>
          <h5 class="mb-25">Defina o gestor (supervisor) de cada representante</h5>
          <p class="mb-0 text-muted small">
            O gestor escolhido passa a ser o supervisor do representante. Essa relação
            é usada, por exemplo, para alocar o Budget do supervisor entre seus representantes.
          </p>
        </div>
      </div>
    </b-card>

    <b-card>
      <b-row class="align-items-end mb-1">
        <b-col md="6" sm="12">
          <label class="small text-muted">Buscar representante</label>
          <b-input-group size="sm">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input v-model="busca" placeholder="Nome ou código..." debounce="250" />
          </b-input-group>
        </b-col>
        <b-col md="6" sm="12" class="text-md-right mt-1 mt-md-0">
          <span class="text-muted small">{{ representantesFiltrados.length }} representante(s)</span>
        </b-col>
      </b-row>

      <div v-if="isLoading" class="text-center py-4"><b-spinner /></div>
      <div v-else-if="!representantesFiltrados.length" class="text-center py-4 text-muted">
        Nenhum representante encontrado.
      </div>
      <b-table
        v-else
        :items="representantesFiltrados"
        :fields="fields"
        responsive
        hover
        small
        class="mb-0"
      >
        <template #cell(representantenome)="data">
          <span class="font-weight-bold">{{ data.item.representantenome }}</span>
          <div class="text-muted" style="font-size: 0.75rem;">#{{ data.item.representanteid }}</div>
        </template>

        <template #cell(representanteativo)="data">
          <b-badge :variant="isAtivo(data.item) ? 'light-success' : 'light-secondary'">
            {{ isAtivo(data.item) ? 'Ativo' : 'Inativo' }}
          </b-badge>
        </template>

        <template #cell(gestorid)="data">
          <b-form-select
            size="sm"
            :value="gestorSelecionado(data.item)"
            @change="val => onGestorChange(data.item, val)"
          >
            <b-form-select-option :value="null">— Sem gestor —</b-form-select-option>
            <b-form-select-option
              v-for="g in gestoresDisponiveis(data.item)"
              :key="g.representanteid"
              :value="String(g.representanteid)"
            >
              {{ g.representantenome }}
            </b-form-select-option>
          </b-form-select>
        </template>

        <template #cell(acoes)="data">
          <b-button
            size="sm"
            variant="relief-primary"
            :disabled="!isDirty(data.item) || isSaving(data.item)"
            @click="salvar(data.item)"
          >
            <b-spinner v-if="isSaving(data.item)" small class="mr-25" />
            <feather-icon v-else icon="SaveIcon" size="13" class="mr-25" />
            Salvar
          </b-button>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BBadge, BButton, BSpinner,
  BFormSelect, BFormSelectOption, BFormInput, BInputGroup, BInputGroupPrepend,
} from 'bootstrap-vue';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import RepresentanteHierarquiaClient from '@/api/Representante/representanteHierarquia.client';

export default {
  name: 'HierarquiaRepresentantes',
  components: {
    BCard, BRow, BCol, BTable, BBadge, BButton, BSpinner,
    BFormSelect, BFormSelectOption, BFormInput, BInputGroup, BInputGroupPrepend,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      isLoading: false,
      busca: '',
      representantes: [],
      gestores: [],
      selecoes: {},
      salvando: {},
      client: new RepresentanteHierarquiaClient(),
      fields: [
        { key: 'representantenome', label: 'Representante' },
        { key: 'representanteativo', label: 'Situação', class: 'text-center' },
        { key: 'gestorid', label: 'Gestor (Supervisor)' },
        { key: 'acoes', label: 'Ações', class: 'text-center' },
      ],
    };
  },
  computed: {
    representantesFiltrados() {
      const q = (this.busca || '').toLowerCase().trim();
      if (!q) return this.representantes;
      return this.representantes.filter(r =>
        (r.representantenome || '').toLowerCase().includes(q)
        || String(r.representanteid).toLowerCase().includes(q),
      );
    },
  },
  async mounted() {
    await this.carregar();
  },
  methods: {
    async carregar() {
      try {
        this.isLoading = true;
        const [{ data: reps }, { data: gst }] = await Promise.all([
          this.client.listar(),
          this.client.listarGestores(),
        ]);
        this.representantes = reps.data || [];
        this.gestores = gst.data || [];
        const sel = {};
        for (const r of this.representantes) {
          sel[r.representanteid] = r.gestorid != null ? String(r.gestorid) : null;
        }
        this.selecoes = sel;
      } finally {
        this.isLoading = false;
      }
    },
    isAtivo(item) {
      return item.representanteativo === 1 || item.representanteativo === true;
    },
    gestorSelecionado(item) {
      const v = this.selecoes[item.representanteid];
      return v != null ? String(v) : null;
    },
    persistido(item) {
      return item.gestorid != null ? String(item.gestorid) : null;
    },
    isDirty(item) {
      return this.gestorSelecionado(item) !== this.persistido(item);
    },
    isSaving(item) {
      return !!this.salvando[item.representanteid];
    },
    gestoresDisponiveis(item) {
      // Um representante não pode ser gestor de si mesmo.
      return this.gestores.filter(g => String(g.representanteid) !== String(item.representanteid));
    },
    onGestorChange(item, val) {
      this.$set(this.selecoes, item.representanteid, val != null ? String(val) : null);
    },
    async salvar(item) {
      const gestorid = this.gestorSelecionado(item);
      try {
        this.$set(this.salvando, item.representanteid, true);
        await this.client.definirGestor(item.representanteid, gestorid);
        // Atualiza o valor persistido localmente.
        const idx = this.representantes.findIndex(r => String(r.representanteid) === String(item.representanteid));
        if (idx !== -1) {
          const gestor = this.gestores.find(g => String(g.representanteid) === String(gestorid));
          this.$set(this.representantes, idx, {
            ...this.representantes[idx],
            gestorid: gestorid || null,
            gestor_nome: gestor ? gestor.representantenome : null,
          });
        }
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Gestor atualizado',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: `Hierarquia de "${item.representantenome}" salva.`,
          },
        });
      } catch (e) {
        // erro já exibido pelo client
      } finally {
        this.$set(this.salvando, item.representanteid, false);
      }
    },
  },
};
</script>

<style scoped>
.page-breadcrumb {
  font-size: 0.85rem;
  color: #6e6b7b;
}
</style>
